import { getFullName, resolveDriverName } from '@klareospace/pharos-lib-utils';
import {
  TASK_ASSIGNMENT_EDITABLE_STATUSES,
  TASK_STATUS,
  type CurrentUserResponse,
  type Task,
  type TaskStatus,
} from '@klareospace/pharos-lib-types';

export { resolveDriverName };

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DraftAssignment {
  carrierId: string;
  driverId: string;
}

/** A select option shape shared by both the Carrier and Driver assignment fields. */
export interface AssignmentSelectOption {
  /** True for a foreign carrier kept visible in `selfAssign` mode but not selectable. */
  disabled?: boolean;
  id: string;
  name: string;
}

/** Minimal shape required to build a driver select option — avoids depending on the full user response type. */
export interface AssignableDriver {
  firstName: string;
  id: string;
  lastName: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Sentinel Select value for "no driver" — Radix Select rejects an empty string value. */
export const NONE_DRIVER_VALUE = '__none__';

/** Sentinel Select value for "no carrier" (unassign) — mirrors `NONE_DRIVER_VALUE`. */
export const NONE_CARRIER_VALUE = '__none__';

export const TASK_ASSIGNMENT_MODE = {
  ASSIGN: 'assign',
  READ_ONLY: 'readOnly',
  SELF_ASSIGN: 'selfAssign',
} as const;
export type TaskAssignmentMode = (typeof TASK_ASSIGNMENT_MODE)[keyof typeof TASK_ASSIGNMENT_MODE];

/** Mirrors the backend's editable-status CAS guard (the API 409s outside this window). */
export const ASSIGNMENT_EDITABLE_STATUSES: ReadonlySet<TaskStatus> = new Set(TASK_ASSIGNMENT_EDITABLE_STATUSES);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Whether the task's current status still allows editing the Carrier & Driver assignment. */
export function isAssignmentEditable(status: TaskStatus): boolean {
  return ASSIGNMENT_EDITABLE_STATUSES.has(status);
}

/** `tasks:assign` wins over `tasks:self-assign`; self-assign additionally requires a resolved own carrier. */
export function resolveMode(
  canAssign: boolean,
  canSelfAssign: boolean,
  currentUser: CurrentUserResponse | null,
): TaskAssignmentMode {
  if (canAssign) return TASK_ASSIGNMENT_MODE.ASSIGN;
  if (canSelfAssign && currentUser?.resolvedCarrier) return TASK_ASSIGNMENT_MODE.SELF_ASSIGN;
  return TASK_ASSIGNMENT_MODE.READ_ONLY;
}

/** Resolves the carrier acceptance badge key from the task status, or undefined when neither applies. */
export function resolveAssignmentBadgeKey(status: Task['status']): 'carrierAccepted' | 'carrierRejected' | undefined {
  if (status === TASK_STATUS.CARRIER_ACCEPTED) return 'carrierAccepted';
  if (status === TASK_STATUS.CARRIER_REJECTED) return 'carrierRejected';
  return undefined;
}

/**
 * Fetched carriers in assign mode; in self-assign, only the own carrier is selectable — the task's
 * current foreign carrier (if different) is kept visible but rendered `disabled`.
 */
export function buildCarrierOptions(
  mode: TaskAssignmentMode,
  fetchedCarriers: AssignmentSelectOption[],
  currentUser: CurrentUserResponse | null,
  task: Task,
): AssignmentSelectOption[] {
  if (mode === TASK_ASSIGNMENT_MODE.ASSIGN) return fetchedCarriers;
  if (mode !== TASK_ASSIGNMENT_MODE.SELF_ASSIGN || !currentUser?.resolvedCarrier) return [];

  const ownCarrier: AssignmentSelectOption = {
    id: currentUser.resolvedCarrier.id,
    name: currentUser.resolvedCarrier.name,
  };
  if (task.carrier == null || task.carrier.id === ownCarrier.id) return [ownCarrier];

  const foreignCarrier: AssignmentSelectOption = { disabled: true, id: task.carrier.id, name: task.carrier.name };
  return [foreignCarrier, ownCarrier];
}

/** Fetched drivers in assign mode; in self-assign, only the current user once the drafted carrier matches their own. */
export function buildDriverOptions(
  mode: TaskAssignmentMode,
  assignedDrivers: AssignableDriver[] | undefined,
  currentUser: CurrentUserResponse | null,
  draftCarrierId: string,
): AssignmentSelectOption[] {
  if (mode === TASK_ASSIGNMENT_MODE.ASSIGN) {
    return (assignedDrivers ?? []).map((d) => ({ id: d.id, name: getFullName(d.firstName, d.lastName) }));
  }
  if (
    mode === TASK_ASSIGNMENT_MODE.SELF_ASSIGN &&
    currentUser !== null &&
    draftCarrierId === currentUser.resolvedCarrier?.id
  ) {
    return [{ id: currentUser.id, name: getFullName(currentUser.firstName, currentUser.lastName) }];
  }
  return [];
}

/**
 * Whether the draft has an unsaved change worth showing Save/Cancel for — never dirty in read-only
 * mode. In `selfAssign` mode, dirty either when the caller drafts himself as driver (self-assign) or
 * clears his own assignment back to the none sentinel (release, `tasks:release`).
 */
export function resolveIsDirty(
  mode: TaskAssignmentMode,
  draft: DraftAssignment,
  persisted: DraftAssignment,
  currentUser: CurrentUserResponse | null,
): boolean {
  if (mode === TASK_ASSIGNMENT_MODE.ASSIGN) {
    return draft.carrierId !== persisted.carrierId || draft.driverId !== persisted.driverId;
  }
  if (mode === TASK_ASSIGNMENT_MODE.SELF_ASSIGN) {
    if (currentUser === null) return false;
    const isSelfAssigning = draft.driverId === currentUser.id && persisted.driverId !== currentUser.id;
    const isReleasing = draft.driverId === NONE_DRIVER_VALUE && persisted.driverId === currentUser.id;
    return isSelfAssigning || isReleasing;
  }
  return false;
}

/** Enabled once dirty; in `selfAssign` mode the drafted carrier must also match the caller's own. */
export function resolveCanSave(
  mode: TaskAssignmentMode,
  draft: DraftAssignment,
  persisted: DraftAssignment,
  currentUser: CurrentUserResponse | null,
): boolean {
  if (!resolveIsDirty(mode, draft, persisted, currentUser)) return false;
  if (mode === TASK_ASSIGNMENT_MODE.SELF_ASSIGN) return draft.carrierId === currentUser?.resolvedCarrier?.id;
  return true;
}

/** Disabled while saving, while no carrier is drafted, or — in `selfAssign` mode — until it matches the caller's own. */
export function resolveDriverFieldDisabled(
  mode: TaskAssignmentMode,
  isPending: boolean,
  draftCarrierId: string,
  currentUser: CurrentUserResponse | null,
): boolean {
  if (isPending) return true;
  const hasNoCarrierDrafted = draftCarrierId.length === 0 || draftCarrierId === NONE_CARRIER_VALUE;
  if (hasNoCarrierDrafted) return true;
  if (mode === TASK_ASSIGNMENT_MODE.SELF_ASSIGN) return draftCarrierId !== currentUser?.resolvedCarrier?.id;
  return false;
}

/** Always editable in `assign` mode; in `selfAssign`, only while no driver is assigned yet or it's already the caller. */
export function resolveDriverFieldEditable(
  mode: TaskAssignmentMode,
  task: Task,
  currentUser: CurrentUserResponse | null,
): boolean {
  if (mode === TASK_ASSIGNMENT_MODE.ASSIGN) return true;
  if (mode !== TASK_ASSIGNMENT_MODE.SELF_ASSIGN) return false;
  const isDriverAssignedToOther = task.driver != null && task.driver.id !== currentUser?.id;
  return !isDriverAssignedToOther;
}
